import React, { useState } from "react";
import Images from "../../../constant/Images";
import { useFormik } from "formik";
import * as Yup from "yup";
import services from "../../../services/services";

const ExportUsersModal = () => {
  const [showModal, setShowModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isReadyToDownload, setIsReadyToDownload] = useState(false);
  const [error, setError] = useState('');
  const [csvUrl, setCsvUrl] = useState('');

  const formik = useFormik({
    initialValues: {
      type: "all",
      startDate: "",
      endDate: "",
    },
    validationSchema: Yup.object().shape({
      type: Yup.string().required(),
      startDate: Yup.date().when("type", {
        is: "range",
        then: (schema) => schema.required("Start date is required"),
      }),
      endDate: Yup.date().when("type", {
        is: "range",
        then: (schema) =>
          schema
            .required("End date is required")
            .min(Yup.ref("startDate"), "End date cannot be before start date"),
      }),
    }),
    onSubmit: async (values) => {
      setIsProcessing(true);
      setIsReadyToDownload(false);
      setError("");
      
      try {
        const { data } = await services.getUsersEmail(values)
        if (data && data?.length > 0) {
            console.log("data.length: ", data.length)
            let csvText = 'Email,CreatedAt\n'
            data.forEach((value: any)=> csvText += `${value.email},${value.createdAt}\n`)
            const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            setCsvUrl(url)
            setIsReadyToDownload(true);
        } else {
            setError("No user found!");
        }
      } catch(error: any) {
        setError(error?.message)
    } finally {
        setIsProcessing(false);
      }
    },
  });

  const handleClose = () => {
        const token = localStorage.getItem("authToken");
    console.log("token: ", token)
    setShowModal(false);
    setIsProcessing(false);
    setIsReadyToDownload(false);
    setError('')
    setCsvUrl('')
    formik.resetForm();
  };

  const handleAfterDownload = () => {
    if (csvUrl) {
        setTimeout(()=> {
            setCsvUrl('')
            URL.revokeObjectURL(csvUrl);
        }, 1500)
    }
  }

  return (
    <>
      {/* Trigger Button */}
      <button
        className="rounded-3xl bg-dimblack flex justify-center items-center w-36 h-10 mt-6"
        type="button"
        onClick={() => setShowModal(true)}
      >
        <img
          className="h-3.5 w-3.5 mr-2"
          src={Images.fileExport}
          alt="download"
        />
        <p className="text-white">Export Users</p>
      </button>

      {showModal && (
        <>
          <div className="justify-center items-center flex fixed inset-0 z-50 outline-none">
            <div className="relative w-[500px] bg-white rounded-xl shadow-lg overflow-hidden">
              
              {/* Header */}
              <div className="flex justify-between items-center bg-lightaqua h-[60px] px-6">
                <h3 className="text-lg font-bold text-center w-full">
                  Export Users
                </h3>
                <button
                  onClick={handleClose}
                  className="absolute right-4"
                >
                  <img src={Images.closecircle} className="h-6 w-6" alt="close" />
                </button>
              </div>

              <div className="px-8 py-6">
                {/* ✅ Ready to Download State */}
                {isReadyToDownload ? (
                  <div className="text-center">
                    <p className="mb-6 text-green-600 font-medium">
                      ✅ Your export is ready!
                    </p>
                    <div className="flex justify-center space-x-4">
                      <button
                        type="button"
                        onClick={handleClose}
                        className="w-[120px] h-[45px] border rounded border-aqua text-aqua"
                      >
                        Close
                      </button>
                      <a
                        href={csvUrl || '#'}
                        download={'user.csv'}
                        onClick={handleAfterDownload}
                        className="w-[180px] h-[45px] flex items-center justify-center border rounded bg-aqua text-white"
                      >
                        Download CSV
                      </a>
                    </div>
                  </div>
                ) : error ? (
                    <div className="text-center">
                    <p className="mb-6 text-red-600 font-medium">
                      {error}
                    </p>
                  </div> ) : (
                  /* 🔄 Normal Form (when not ready to download) */
                  <form onSubmit={formik.handleSubmit}>
                    {/* Export Type Selection */}
                    <div className="mb-5">
                      <label className="block mb-2 font-medium">
                        Export Type
                      </label>
                      <select
                        name="type"
                        value={formik.values.type}
                        onChange={formik.handleChange}
                        disabled={isProcessing}
                        className="border border-gray-300 rounded w-full px-3 py-2 focus:outline-none focus:border-aqua disabled:bg-gray-100"
                      >
                        <option value="all">All Users</option>
                        <option value="range">Select Date Range</option>
                      </select>
                    </div>

                    {/* Date Range Fields */}
                    {formik.values.type === "range" && (
                      <>
                        <div className="mb-5">
                          <label className="block mb-2 font-medium">Start Date</label>
                          <input
                            type="date"
                            name="startDate"
                            value={formik.values.startDate}
                            onChange={formik.handleChange}
                            disabled={isProcessing}
                            className="border border-gray-300 rounded w-full px-3 py-2 focus:outline-none focus:border-aqua disabled:bg-gray-100"
                          />
                          {formik.errors.startDate && formik.touched.startDate && (
                            <p className="text-red-500 text-sm mt-1">{formik.errors.startDate}</p>
                          )}
                        </div>

                        <div className="mb-5">
                          <label className="block mb-2 font-medium">End Date</label>
                          <input
                            type="date"
                            name="endDate"
                            value={formik.values.endDate}
                            onChange={formik.handleChange}
                            disabled={isProcessing}
                            className="border border-gray-300 rounded w-full px-3 py-2 focus:outline-none focus:border-aqua disabled:bg-gray-100"
                          />
                          {formik.errors.endDate && formik.touched.endDate && (
                            <p className="text-red-500 text-sm mt-1">{formik.errors.endDate}</p>
                          )}
                        </div>
                      </>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end space-x-4 mt-6">
                      {isProcessing ? (
                        <p className="text-aqua font-medium">Processing...</p>
                      ) : (
                        <>
                          <button
                            type="button"
                            onClick={handleClose}
                            disabled={isProcessing}
                            className="w-[120px] h-[45px] border rounded border-aqua text-aqua disabled:bg-gray-100"
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            disabled={isProcessing}
                            className="w-[180px] h-[45px] border rounded bg-aqua text-white disabled:bg-gray-300"
                          >
                            Export
                          </button>
                        </>
                      )}
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>

          {/* Overlay */}
          <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
        </>
      )}
    </>
  );
};

export default ExportUsersModal;
